<script setup lang="ts">
import { computed, inject, onBeforeUnmount, ref, watch } from "vue";
import { UPLOAD_PORT } from "@/shared/contracts/ports";
import { type DocumentVersion, readDocument } from "../api/document-content";
import { useAgentsI18n } from "../i18n/use-agents-i18n";

const props = defineProps<{ selected: DocumentVersion; other: DocumentVersion; content: string }>();
const { t } = useAgentsI18n();
const uploads = inject(UPLOAD_PORT, null);
const otherContent = ref("");
const loading = ref(false);
const failed = ref(false);
let controller: AbortController | null = null;
watch(
	() => props.other.id,
	async (id) => {
		controller?.abort();
		const current = new AbortController();
		controller = current;
		loading.value = true;
		failed.value = false;
		try {
			if (!uploads) throw Error("no upload port");
			const result = await readDocument(uploads, id, current.signal);
			if (!current.signal.aborted) otherContent.value = result;
		} catch {
			if (!current.signal.aborted) failed.value = true;
		} finally {
			if (!current.signal.aborted) loading.value = false;
		}
	},
	{ immediate: true },
);
onBeforeUnmount(() => controller?.abort());
const columns = computed(() =>
	[
		{ version: props.selected.version, content: props.content },
		{ version: props.other.version, content: otherContent.value },
	].sort((a, b) => a.version - b.version),
);
</script>
<template>
 <p v-if="loading" role="status" class="p-4 text-sm">{{ t('document.loading') }}</p>
 <p v-else-if="failed" role="alert" class="p-4 text-sm text-danger">{{ t('document.failed') }}</p>
 <div v-else class="grid min-h-0 flex-1 grid-cols-2 overflow-hidden" :aria-label="t('document.compare')">
 <section v-for="column in columns" :key="column.version" class="min-w-0 overflow-auto border-r border-border p-3">
 <h3 class="sticky top-0 mb-3 bg-surface text-xs font-semibold">{{ t('document.version',{version:column.version}) }}</h3>
 <pre class="whitespace-pre-wrap break-words font-mono text-xs">{{ column.content }}</pre>
 </section>
 </div>
</template>
